import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faLocationDot,
  faLacrosseStickBall,
  faPenLine,
  faUserGroup,
  faCircleCheck,
  faArrowRight,
} from "@fortawesome/pro-solid-svg-icons";
import {
  buildParticleCss,
  PARTICLE_BG,
  PARTICLE_SHADOW,
  PARTICLES,
} from "./particleDissolve";

/**
 * Ambient mockup of the Member Portal "New Reservation" flow.
 * Active sequence: rows populate top-to-bottom, save button pulses, success
 * toast slides in ("Reservation created"), then the send-off plays — a field
 * of cyan particles flies in from the edges, settles over the card, and is
 * swept off the right edge in a gust while the card stays put.
 * Resting state: form remains populated; the toast's check icon pulses slowly.
 * Transform/opacity only; honors prefers-reduced-motion.
 */

const SURFACE = "#1F1F1F";
const DIVIDER = "#2E2E2E";
const TOAST_BG = "#252525";
const TOAST_BORDER = "#333333";
const TEAL = "#44C2E3";
const LIME = "#C0FF00";

const ROWS = [
  { icon: faCalendar, label: "Sat, May 24 · 4:00 PM" },
  { icon: faLocationDot, label: "Field 2" },
  { icon: faLacrosseStickBall, label: "Lacrosse" },
  { icon: faPenLine, label: "Highlight Reel + Photos" },
  { icon: faUserGroup, label: "Varsity" },
];

// Send-off timing: the particle dissolve plays once, after the ~6s populate
// build-up, then holds on the created state until the section cycle re-activates
// the card. The active window must be ≥ SEND_DELAY_MS + SEND_MS.
const POPULATE_MS = 6000;
const SEND_DELAY_MS = POPULATE_MS + 600; // populate + a brief hold
const SEND_MS = 5000;

const CSS = `
/* Active keyframes (each runs once over 6s, ending in populated state) */
@keyframes hiw-sched-header { 0%, 4% { opacity: 0; transform: translateY(-4px); } 10%, 100% { opacity: 1; transform: translateY(0); } }
@keyframes hiw-sched-row-1 { 0%, 7% { opacity: 0; transform: translateY(6px); } 17%, 100% { opacity: 1; transform: translateY(0); } }
@keyframes hiw-sched-row-2 { 0%, 17% { opacity: 0; transform: translateY(6px); } 27%, 100% { opacity: 1; transform: translateY(0); } }
@keyframes hiw-sched-row-3 { 0%, 27% { opacity: 0; transform: translateY(6px); } 37%, 100% { opacity: 1; transform: translateY(0); } }
@keyframes hiw-sched-row-4 { 0%, 37% { opacity: 0; transform: translateY(6px); } 47%, 100% { opacity: 1; transform: translateY(0); } }
@keyframes hiw-sched-row-5 { 0%, 47% { opacity: 0; transform: translateY(6px); } 57%, 100% { opacity: 1; transform: translateY(0); } }
@keyframes hiw-sched-button {
  0%, 60% { opacity: 0; transform: scale(0.96); }
  65% { opacity: 1; transform: scale(1); }
  70% { transform: scale(1.04); }
  75%, 100% { opacity: 1; transform: scale(1); }
}
@keyframes hiw-sched-toast {
  0%, 75% { opacity: 0; transform: translate(12px, 0); }
  80%, 100% { opacity: 1; transform: translate(0, 0); }
}

/* Resting ambient: slow gentle pulse on the toast check icon */
@keyframes hiw-sched-ambient-check {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.18); }
}

/* Camera - a gentle, centered zoom (max ~1.12x) that drifts down the form as
   rows populate, then eases back to 1x for the success moment so the completed
   form, the Create Reservation button and the toast are all in frame. */
@keyframes hiw-sched-camera {
  0%, 12% { transform: scale(1); transform-origin: 50% 50%; }
  30% { transform: scale(1.1); transform-origin: 50% 40%; }
  50% { transform: scale(1.12); transform-origin: 50% 52%; }
  64% { transform: scale(1.12); transform-origin: 50% 62%; }
  80%, 100% { transform: scale(1); transform-origin: 50% 50%; }
}

.hiw-anim { will-change: transform, opacity; }
.hiw-sched-header { animation: hiw-sched-header 6s ease-out forwards; }
.hiw-sched-row-1 { animation: hiw-sched-row-1 6s ease-out forwards; }
.hiw-sched-row-2 { animation: hiw-sched-row-2 6s ease-out forwards; }
.hiw-sched-row-3 { animation: hiw-sched-row-3 6s ease-out forwards; }
.hiw-sched-row-4 { animation: hiw-sched-row-4 6s ease-out forwards; }
.hiw-sched-row-5 { animation: hiw-sched-row-5 6s ease-out forwards; }
.hiw-sched-button { animation: hiw-sched-button 6s ease-out forwards; }
.hiw-sched-toast { animation: hiw-sched-toast 6s ease-out forwards; }

.hiw-sched-ambient-check { animation: hiw-sched-ambient-check 2.8s ease-in-out infinite; }

.hiw-sched-camera { animation: hiw-sched-camera 6s cubic-bezier(0.4, 0, 0.2, 1) forwards; will-change: transform; }

@media (prefers-reduced-motion: reduce) {
  .hiw-anim, .hiw-sched-camera { animation: none !important; opacity: 1 !important; transform: none !important; }
}

/* ── Send-off: particle dissolve (plays once, after the populate) ───────── */
${buildParticleCss(SEND_MS, SEND_DELAY_MS)}
`;

interface Props {
  isActive: boolean;
}

const ScheduleAnimation = ({ isActive }: Props) => {
  const A = (activeCls: string) => (isActive ? `hiw-anim ${activeCls}` : "");

  return (
    <div
      className="hiw-sched-root relative aspect-[4/3] overflow-hidden"
      style={{ background: SURFACE, contain: "paint", containerType: "inline-size" }}
    >
      <style>{CSS}</style>

      <div className={`absolute inset-0 ${isActive ? "hiw-anim hiw-sched-camera" : ""}`}>
        {/* Header bar */}
        <div
          className={`absolute inset-x-0 top-0 h-11 flex items-center justify-between px-5 ${A("hiw-sched-header")}`}
          style={{ borderBottom: `1px solid ${DIVIDER}` }}
        >
          <div className="flex gap-2">
            <span className="block w-2 h-2 rounded-full" style={{ background: "#3A3A3A" }} />
            <span className="block w-2 h-2 rounded-full" style={{ background: "#3A3A3A" }} />
            <span className="block w-2 h-2 rounded-full" style={{ background: "#3A3A3A" }} />
          </div>
          <span className="text-[13px] font-black text-white tracking-tight">
            New Reservation
          </span>
          <span className="w-10" />
        </div>

        {/* Body */}
        <div className="absolute inset-x-4 top-14 bottom-4 flex flex-col">
          {ROWS.map((row, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-2 py-2.5 ${i < ROWS.length - 1 ? "border-b" : ""} ${A(`hiw-sched-row-${i + 1}`)}`}
              style={{ borderColor: DIVIDER }}
            >
              <FontAwesomeIcon icon={row.icon} className="w-3.5 h-3.5 shrink-0" style={{ color: TEAL }} />
              <span className="text-[12px] font-bold text-white truncate">{row.label}</span>
            </div>
          ))}

          {/* Save button */}
          <div
            className={`mt-auto self-start flex items-center gap-2 rounded-full pl-4 pr-3 py-2 ${A("hiw-sched-button")}`}
            style={{ background: LIME }}
          >
            <span className="text-[12px] font-black text-black tracking-tight">Create Reservation</span>
            <span
              className="flex items-center justify-center w-5 h-5 rounded-full"
              style={{ background: "rgba(0,0,0,0.18)" }}
            >
              <FontAwesomeIcon icon={faArrowRight} className="w-2.5 h-2.5" style={{ color: "#000" }} />
            </span>
          </div>
        </div>

        {/* Success toast */}
        <div
          className={`absolute bottom-4 right-4 flex items-center gap-2 rounded-full pl-3 pr-3.5 py-2 ${A("hiw-sched-toast")}`}
          style={{
            background: TOAST_BG,
            border: `1px solid ${TOAST_BORDER}`,
            boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
          }}
        >
          <FontAwesomeIcon
            icon={faCircleCheck}
            className="w-4 h-4 hiw-anim hiw-sched-ambient-check"
            style={{ color: TEAL }}
          />
          <span className="text-[11px] font-bold text-white">Reservation created</span>
        </div>
      </div>

      {/* Send-off: particles fly in from the edges, settle over the card, then
          get swept off the right edge (active path only). */}
      {isActive &&
        PARTICLES.map((p, i) => (
          <span
            key={i}
            className={`hiw-sched-particle hiw-sched-particle-${i} absolute rounded-full pointer-events-none`}
            style={{
              top: `${p.sy}%`,
              left: `${p.sx}%`,
              width: p.size,
              height: p.size,
              transform: "translate(-50%, -50%)",
              background: PARTICLE_BG,
              boxShadow: PARTICLE_SHADOW,
            }}
          />
        ))}
    </div>
  );
};

export default ScheduleAnimation;
